import Milestone from "../../../model/Milestone";
import * as React from "react";
import {useEffect, useState} from "react";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {useResources} from "../../../stores/ResourceProvider";
import DatePickerValidator from "../../../components/form/DatePickerValidator";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import Model from "../../../model/Model";
import Tracking from "../../../components/Tracking";
import {getISODateFromDate, isoToLocalDate} from "../../../stores/StoreUtilities";
import {CreateMilestoneInput, UpdateMilestoneInput} from "../../../API";
import {isDate} from "date-fns";
import {Box, Typography, useTheme} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";

const MilestoneEditDialog = ({
  open,
  milestone,
  model,
  onClose,
  onSave,
  onDelete
}: {
  open?: boolean
  milestone: Milestone
  model: Model
  onClose?(): void
  onSave?(update: Milestone): void
  onDelete?(milestone: Milestone): void
}) => {
  const [label, setLabel] = useState<string>("")
  const [date, setDate] = useState<Date | null | undefined>(undefined)
  const [isLocked, setIsLocked] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const { modelStore, notify, confirm } = useResources()

  const theme = useTheme()

  useEffect(() => {
    setLabel(milestone.label ?? "")
    setDate(isoToLocalDate(milestone.date) ?? undefined)
    if (model.hasLock(milestone.id, "*") || model.isLocked()) {
      setIsLocked(true)
    }
    setIsOpen(open === true)
  }, [milestone, open])

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async () => {
    try {
      const trimmedLabel = label.trim()
      const isCreate = !milestone.createdAt
      const labelChanged = milestone.label !== trimmedLabel
      const dateChanged = (milestone.date ?? "") !== (isDate(date) ? getISODateFromDate(date as Date) : "")

      let updated: Milestone | undefined = milestone
      if (isCreate) {
        const input: CreateMilestoneInput = {
          id: milestone.id,
          userId: milestone.userId,
          accountId: milestone.accountId,
          modelId: milestone.modelId,
          label: trimmedLabel,
          description: milestone.description,
          archived: milestone.archived,
          date: date ? getISODateFromDate(date) : null,
          age: milestone.age
        }
        updated = await modelStore.createMilestone(input)
        Tracking.event({action: "Milestone Created"})
      } else if (labelChanged || dateChanged) {
        const input: UpdateMilestoneInput = {
          id: milestone.id,
          label: trimmedLabel,
          date: date ? getISODateFromDate(date) : null
        }
        updated = await modelStore.updateMilestone(input)
        Tracking.event({action: "Milestone Updated"})
      }

      if (onSave && updated) {
        onSave(updated)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const handleDelete = async () => {
    if (!onDelete) {
      return
    }
    confirm.show("Confirm Delete", `Are you sure you want to delete this milestone?`,
      ['Delete', 'Cancel'],
      async () => {
        try {
          if (milestone.createdAt) {
            const deleted = await modelStore.deleteMilestone(milestone.id)
            if (deleted) {
              onDelete(deleted)
            }
          } else {
            onDelete(milestone)
          }
          return true
        } catch (err: any) {
          notify.show('error', err.message)
          return false
        }
      })
  }

  const renderLock = () => {
    if (isLocked) {
      let lockMsg = model.getLock(model.id, "*")
      if (!lockMsg) {
        lockMsg = model.getLock(milestone.id, "*")
      }
      return (
        <Box display="flex" justifyContent="flex-start" alignItems="center" mt={1}>
          <LockIcon sx={{color: theme.palette.primary.main, mr:1}}/>
          <Typography variant="body2" color={theme.palette.primary.main}>
            {lockMsg}
          </Typography>
        </Box>
      )

    } else {
      return null
    }
  }


  if (!isOpen) {
    return null
  }

  return (
    <ModelEditDialog title="Edit Milestone" open={isOpen} size="sm"
                     onCancel={handleClose}
                     onSave={!isLocked ? handleSave : undefined}
                     onDelete={milestone.isCustom && !isLocked ? handleDelete : undefined}
                     confirmDelete={false}
    >
      {renderLock()}
      <TextFieldValidator
        margin="normal"
        name="label"
        label="Label"
        type="text"
        fullWidth
        variant="standard"
        required
        autoFocus
        validators={{ required: true }}
        value={label}
        disabled={isLocked}
        onChange={(event: any) => setLabel(event.target.value)}
      />
      <DatePickerValidator
        name="date"
        label="Date"
        value={date}
        format="MM-dd-yyyy"
        variant="standard"
        margin="dense"
        fullWidth
        required
        disabled={isLocked}
        onChange={(newValue: any) => setDate(newValue)}
      />
    </ModelEditDialog>
  )
}

export default MilestoneEditDialog
